import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

import { useTheme } from '../../../../contexts/ThemeContext';
import type { CycleErrorData } from '../types';

/**
 * CycleErrorNode — emitted by topologyWalk when MAX_DEPTH cycle bailout fires.
 *
 * Rose-bordered chip at the bailout (x,y). Auto-clears on next render
 * — the walker re-emits per layoutDag call, so a transient cycle that
 * resolves on the next render simply vanishes (no persistence layer).
 *
 * Rate-limit (one cycle_error per parentId per layoutDag call) lives
 * in topologyWalk.ts (Task 3.2) — prevents flicker storms under WS
 * re-renders.
 */
const CycleErrorNode: React.FC<NodeProps<CycleErrorData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const shortParent = (data.parentId ?? '').slice(0, 8);

  return (
    <div
      className={`nodrag rounded-md border-2 px-2 py-1 text-[10px] select-none rose ${
        darkMode
          ? 'bg-rose-950 border-rose-500 text-rose-200'
          : 'bg-rose-50 border-rose-400 text-rose-800'
      }`}
      style={{ width: 240 }}
      data-testid={`cycle-error-${data.parentId}`}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <div className="font-medium">⚠ Cycle in parent chain</div>
      <div className="text-[9px] mt-0.5">chain truncated · {shortParent}</div>
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default CycleErrorNode;
